import { useSuspenseQuery } from '@tanstack/react-query'
import { useConfig } from 'wagmi'

import { SuspenseQueryWith } from '@/utils/types'

import { QUERY_STALE_TIME } from '@/config/consts'
import { usdxlEmaConfig } from '@/config/contracts-generated'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { queryOptions } from '@tanstack/react-query'
import { Config } from 'wagmi'
import { multicall } from 'wagmi/actions'
import { getContractAddress } from '../hooks/useContractAddress'
import { formatUnits } from 'viem'

export interface UseUsdxlEmaOracleParams {
  chainId: number
  refetchInterval?: number
}

interface UsdxlEmaOracleResult {
  decimals: number
  price: NormalizedUnitNumber
  formattedPrice: NormalizedUnitNumber
}

export type UseUsdxlEmaOracleResult = SuspenseQueryWith<{
  data: UsdxlEmaOracleResult
}>

export function useUsdxlEmaOracle({ chainId, refetchInterval }: UseUsdxlEmaOracleParams): UseUsdxlEmaOracleResult {
  const wagmiConfig = useConfig()

  const res = useSuspenseQuery({
    ...usdxlEmaOracleQueryOptions({
      wagmiConfig,
      chainId,
    }),
    refetchInterval,
    staleTime: QUERY_STALE_TIME,
  })

  return {
    ...res,
    data: res.data,
  }
}

interface UsdxlEmaOracleParams {
  wagmiConfig: Config
  chainId: number
}

export function usdxlEmaOracleQueryOptions({ wagmiConfig, chainId }: UsdxlEmaOracleParams) {
  return queryOptions<UsdxlEmaOracleResult>({
    queryKey: usdxlEmaOracleQueryKey({ chainId }),
    queryFn: async () => {
      const usdxlEmaAddress = getContractAddress(usdxlEmaConfig.address, chainId)
      const [decimals, price] = await multicall(wagmiConfig, {
        chainId,
        allowFailure: false,
        contracts: [
          {
            address: usdxlEmaAddress,
            args: [],
            abi: usdxlEmaConfig.abi,
            functionName: 'decimals',
          },
          {
            address: usdxlEmaAddress,
            args: [],
            abi: usdxlEmaConfig.abi,
            functionName: 'latestAnswer',
          },
        ],
      })
      return {
        decimals,
        price: NormalizedUnitNumber(price),
        formattedPrice: NormalizedUnitNumber(formatUnits(price, decimals)),
      }
    },
  })
}

export function usdxlEmaOracleQueryKey({ chainId }: Omit<UsdxlEmaOracleParams, 'wagmiConfig'>): unknown[] {
  return ['usdxl-ema', chainId]
}
